import * as React from "react";
import { useContext } from "react";
import { Drawer as BaseDrawer } from "@base-ui/react/drawer";
import Box from "@sproutsocial/seeds-react-box";
import Button from "@sproutsocial/seeds-react-button";
import Icon from "@sproutsocial/seeds-react-icon";
// eslint-disable-next-line import/no-deprecated
import Text from "@sproutsocial/seeds-react-text";
import { useIsMobile } from "@sproutsocial/seeds-react-hooks";
import { DisablePortalToBodyContext } from "@sproutsocial/seeds-react-portal";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import { Content } from "./styles";
import { ActionRail, ActionRailButton } from "./ActionRail";
import type {
  TypeDrawerContext,
  TypeDrawerCloseButtonProps,
  TypeDrawerHeaderProps,
  TypeDrawerProps,
  TypeDrawerContentProps,
} from "./DrawerTypes";

/**
 * Single source of truth for the Drawer context. Both the Tailwind and the
 * styled-components shells provide this exact context, and the shared
 * sub-components (`Drawer.CloseButton`, `Drawer.Header`) consume it — so there
 * must be exactly one context identity, or the close button can't see its
 * provider.
 */
export const DrawerContext = React.createContext<TypeDrawerContext>({
  isLocked: false,
  setIsLocked: () => {},
  hasActionRail: false,
});

// Utility for merging class names properly. Copied from DrawerTailwind.tsx —
// `cn` is NOT exported from @sproutsocial/seeds-react-utilities, so each
// Tailwind component carries its own copy.
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const DrawerCloseButton = (props: TypeDrawerCloseButtonProps) => {
  const drawerContext = useContext(DrawerContext);

  if (props.render) {
    return props.render(drawerContext) ?? null;
  }

  return (
    <Button
      appearance="pill"
      aria-label={drawerContext.closeButtonLabel}
      onClick={drawerContext.onClose}
      {...props}
    >
      {props.children || <Icon aria-hidden name="x-outline" />}
    </Button>
  );
};

/**
 * Styled-components header — the always-styled implementation, preserved for
 * backward compatibility. The hybrid router below sends consumers here whenever
 * they pass styled-system props (e.g. `<Drawer.Header alignItems="center">`) or
 * a `styled()` extension, so the full `Box` prop surface keeps working.
 */
const DrawerHeaderStyled = ({
  title = "",
  id = undefined,
  children,
  render,
  ...rest
}: TypeDrawerHeaderProps) => {
  const drawerContext = useContext(DrawerContext);

  if (render) {
    return render(drawerContext);
  }

  return (
    <Box
      display="flex"
      flex="0 0 auto"
      justifyContent="space-between"
      alignItems="center"
      pt={400}
      px={450}
      {...rest}
    >
      {children || (
        <>
          <Text
            as="h2"
            fontSize={400}
            fontWeight="semibold"
            color="text.headline"
            id={id}
          >
            {title}
          </Text>
          {!drawerContext.hasActionRail && <DrawerCloseButton />}
        </>
      )}
    </Box>
  );
};

/**
 * Tailwind/CSS-class header — the preferred path, routed to by the hybrid when
 * the consumer passes no styled-system props. Renders a plain `<div>` with the
 * `seeds-drawer-header` classes from `drawer.css` instead of a styled `Box`.
 * The `render` prop is handled by the hybrid before reaching here.
 */
const DrawerHeaderTailwind = ({
  title = "",
  id = undefined,
  children,
  render,
  ...rest
}: TypeDrawerHeaderProps) => {
  const drawerContext = useContext(DrawerContext);
  const { className, ...domRest } = rest as {
    className?: string;
  } & Record<string, unknown>;

  return (
    <div className={cn("seeds-drawer-header", className)} {...domRest}>
      {children || (
        <>
          <h2 className="seeds-drawer-header-title" id={id}>
            {title}
          </h2>
          {!drawerContext.hasActionRail && <DrawerCloseButton />}
        </>
      )}
    </div>
  );
};

/**
 * Hybrid header router. Chooses the styled path when the consumer passes
 * styled-system props or a `styled()` extension, else the Tailwind path —
 * independent of the root Drawer's routing. The `render` prop short-circuits
 * before any routing, matching the original always-styled behaviour.
 */
export const DrawerHeader = (props: TypeDrawerHeaderProps) => {
  const drawerContext = useContext(DrawerContext);

  if (props.render) {
    return props.render(drawerContext);
  }

  const { title, id, children, render, ...rest } = props;

  if (needsStyledComponents(rest)) {
    return <DrawerHeaderStyled {...props} />;
  }

  return <DrawerHeaderTailwind {...props} />;
};

/**
 * Styled-components content — the always-styled implementation, preserved for
 * backward compatibility. The hybrid router sends consumers here whenever they
 * pass styled-system props (e.g. `<Drawer.Content color="text.body">`).
 */
const DrawerContentStyled = ({ children, ...rest }: TypeDrawerContentProps) => (
  <Content
    height="100%"
    p={450}
    color="text.body"
    data-drawer-content=""
    {...rest}
  >
    {children}
  </Content>
);

/**
 * Tailwind/CSS-class content — the preferred path. Renders a plain
 * `<div data-drawer-content>` with the `seeds-drawer-content` class from
 * `drawer.css` instead of a styled `Box`.
 */
const DrawerContentTailwind = ({
  children,
  ...rest
}: TypeDrawerContentProps) => {
  const { className, ...domRest } = rest as {
    className?: string;
  } & Record<string, unknown>;

  return (
    <div
      className={cn("seeds-drawer-content", className)}
      data-drawer-content=""
      {...domRest}
    >
      {children}
    </div>
  );
};

/**
 * Hybrid content router. Chooses the styled path when the consumer passes
 * styled-system props or a `styled()` extension, else the Tailwind path.
 */
export const DrawerContent = (props: TypeDrawerContentProps) => {
  const { children, ...rest } = props;

  if (needsStyledComponents(rest)) {
    return <DrawerContentStyled {...props} />;
  }

  return <DrawerContentTailwind {...props} />;
};

DrawerHeader.displayName = "Drawer.Header";
DrawerContent.displayName = "Drawer.Content";
DrawerCloseButton.displayName = "Drawer.CloseButton";

/**
 * Root-level Drawer logic shared by both shells. Centralising it here (rather
 * than duplicating it in each shell) guarantees the two implementations stay
 * behaviourally identical — the esc-key lock and exit-freeze logic is exactly
 * what the lock/close tests exercise.
 */
export const useDrawerShell = (props: TypeDrawerProps) => {
  const {
    children,
    closeButtonLabel,
    direction = "right",
    disableCloseOnClickOutside = false,
    id,
    modal = false,
    open,
    offset = 0,
    onClose,
    zIndex = 7,
    width = 600,
    snapPoints,
    defaultSnapPoint,
    snapPoint,
    onSnapPointChange,
    snapToSequentialPoints,
    actions,
    actionsInHeader = false,
    ...rest
  } = props;

  const isMobile = useIsMobile();
  const effectiveDirection = isMobile ? "bottom" : direction;
  const effectiveSnapPoints =
    effectiveDirection === "bottom" ? snapPoints : undefined;
  const hasSnapPoints = !!effectiveSnapPoints && effectiveSnapPoints.length > 0;
  const hasActionRail =
    isMobile && !actionsInHeader && !!actions && actions.length > 0;
  const [isLocked, setIsLocked] = React.useState(false);

  const ariaLabelledBy = rest["aria-labelledby"];
  const ariaLabel = rest["aria-label"];
  React.useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      if (!ariaLabelledBy && !ariaLabel) {
        console.warn(
          "[Drawer] Missing accessible name. Add aria-labelledby pointing to your Drawer.Header id, or aria-label, so screen readers can identify the dialog."
        );
      }
    }
  }, [ariaLabelledBy, ariaLabel]);
  // Keep a ref so the onOpenChange callback always sees the current isLocked
  // value without needing to be re-created on each render.
  const isLockedRef = React.useRef(isLocked);
  isLockedRef.current = isLocked;

  // Freeze the last-rendered children during the exit transition so consumers
  // that conditionally render content (e.g. {isOpen && <Content />}) don't
  // produce an empty drawer shell while Base UI animates the popup out.
  const prevChildrenRef = React.useRef<React.ReactNode>(null);
  if (open) {
    prevChildrenRef.current = children;
  }
  const renderedChildren = open ? children : prevChildrenRef.current;

  const rootProps: Omit<
    React.ComponentProps<typeof BaseDrawer.Root>,
    "children"
  > = {
    open,
    onOpenChange: (isOpen, eventDetails) => {
      if (!isOpen) {
        if (isLockedRef.current && eventDetails.reason === "escape-key") {
          return;
        }
        onClose();
      }
    },
    disablePointerDismissal: disableCloseOnClickOutside,
    swipeDirection:
      effectiveDirection === "bottom" ? "down" : effectiveDirection,
    modal,
    snapPoints: effectiveSnapPoints,
    defaultSnapPoint,
    snapPoint,
    onSnapPointChange: onSnapPointChange
      ? (sp) => onSnapPointChange(sp)
      : undefined,
    snapToSequentialPoints,
  };

  return {
    effectiveDirection,
    hasSnapPoints,
    hasActionRail,
    isLocked,
    setIsLocked,
    renderedChildren,
    rootProps,
    rest,
    width,
    offset,
    zIndex,
    id,
    open,
    closeButtonLabel,
    onClose,
    actions,
  };
};

export type TypeDrawerShell = ReturnType<typeof useDrawerShell>;

/**
 * The popup contents shared by both shells: the optional mobile action rail and
 * the context providers wrapping the rendered children. Only the popup wrapper
 * and drag handle differ between the Tailwind and styled paths — everything
 * inside the popup is identical, so it lives here.
 */
export const DrawerPopupContents = ({ shell }: { shell: TypeDrawerShell }) => {
  const {
    hasActionRail,
    closeButtonLabel,
    onClose,
    actions,
    isLocked,
    setIsLocked,
    renderedChildren,
  } = shell;

  return (
    <>
      {hasActionRail && actions && (
        <ActionRail isMobile aria-label="Drawer quick actions">
          <ActionRailButton
            aria-label={closeButtonLabel}
            onClick={onClose}
            data-qa-drawer-rail-close
          >
            <Icon aria-hidden name="x-outline" />
          </ActionRailButton>
          {actions.map((action, i) => (
            <ActionRailButton
              key={i}
              aria-label={action["aria-label"]}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.iconName && <Icon aria-hidden name={action.iconName} />}
            </ActionRailButton>
          ))}
        </ActionRail>
      )}
      <DisablePortalToBodyContext.Provider value={true}>
        <DrawerContext.Provider
          value={{
            onClose,
            closeButtonLabel,
            isLocked,
            setIsLocked,
            hasActionRail,
          }}
        >
          {renderedChildren}
        </DrawerContext.Provider>
      </DisablePortalToBodyContext.Provider>
    </>
  );
};
